import { FieldSpec, InstanceProperties, RichType } from 'lib/rich'
import { UnitShorthand } from 'types/unit-shorthand'
import { groups, type_names } from '@toolpath/tools'
import { unit_spec } from './common'

const coolantEnumValues = [
  { value: 'disabled', json_value: 'disabled', title: 'Disabled' },
  { value: 'flood', json_value: 'flood', title: 'Flood' },
  { value: 'mist', json_value: 'mist', title: 'Mist' },
  { value: 'tool', json_value: 'tool', title: 'Through Tool' },
  { value: 'air', json_value: 'air', title: 'Air' },
  {
    value: 'airThroughTool',
    json_value: 'air through tool',
    title: 'Air through tool',
  },
  { value: 'suction', json_value: 'suction', title: 'Suction' },
  { value: 'floodMist', json_value: 'flood mist', title: 'Flood and mist' },
  {
    value: 'floodTool',
    json_value: 'flood tool',
    title: 'Flood and through tool',
  },
]

type ToolType = keyof typeof type_names
type GroupType = keyof typeof groups

function expandForTypes(for_types) {
  return for_types.flatMap((ft) => groups[ft] || ft)
}

function typeIn(...types: (ToolType | GroupType)[]): {
  type: { in: ToolType[] }
} {
  return { type: { in: expandForTypes(types) } }
}

const typeEnumValues: { value: ToolType; title: string }[] = (
  Object.entries(type_names) as [ToolType, string][]
).map(([value, title]) => ({ value, title }))

const showToolProps = false

export class FSPreset extends RichType {
  /////////// context - tool parameters needed in compute functions ////////////
  // TODO: Move to __parent

  //__parent?: Tool

  static unit_spec: FieldSpec = { ...unit_spec, hidden: !showToolProps }
  unit: UnitShorthand = 'mm'

  static type_spec: FieldSpec = {
    title: 'Tool Type (not shown)',
    kind: 'enum',
    hidden: !showToolProps,
    enumValues: typeEnumValues,
    defaultValue: typeEnumValues[0].value,
  }
  type?: ToolType

  static geometry_cuttingDiameter_spec: FieldSpec = {
    title: 'Tool Dia (not shown)',
    kind: 'length',
    hidden: !showToolProps,
    defaultValueMM: 10,
    defaultValueIN: 0.4,
  }
  geometry_cuttingDiameter?: number

  static geometry_numberOfFlutes_spec: FieldSpec = {
    title: 'Tool #flutes (not shown)',
    kind: 'integer',
    hidden: !showToolProps,
    defaultValue: 3,
    enabledWhen: typeIn('ToolMilling', 'ToolHoleMaking'),
  }
  geometry_numberOfFlutes?: number

  //////////// preset ////////////

  static name_spec: FieldSpec = {
    title: 'Name',
    kind: 'text',
    noLabel: true,
    required: false,
    hidden: false,
  }
  name: string = 'Default preset'

  guid?: string // Tool
  static guid_spec: FieldSpec = {
    title: 'GUID',
    kind: 'guid',
    hidden: true,
    //required: true,
  }

  coolant?: string /*Enum*/
  static coolant_spec: FieldSpec = {
    title: 'Coolant',
    kind: 'enum',
    enumValues: coolantEnumValues,
    defaultValue: 'flood',
    enabledWhen: typeIn('ToolMilling', 'ToolHoleMaking', 'ToolTurning'),
    required: true,
  }

  //////////// preset_speeds ////////////

  static groupSpeed_spec: FieldSpec = {
    title: 'Speed',
    kind: 'group',
  }

  useConstantSurfaceSpeed?: boolean
  static useConstantSurfaceSpeed_spec: FieldSpec = {
    title: 'Use Constant Surface Speed',
    kind: 'boolean',
    defaultValue: true,
    enabledWhen: typeIn(
      'turning general',
      'turning boring',
      'turning grooving',
    ),
    required: true,
  }

  spindleSpeed: number = 5000
  static spindleSpeed_spec: FieldSpec = {
    title: 'Spindle Speed',
    kind: 'rotation_speed',
    defaultValue: 5000,
    enabledWhen: typeIn(
      'ToolMilling',
      'ToolHoleMaking',
      'turning general',
      'turning boring',
      'turning threading',
    ),
    gt: 0,
    required: true,
    /*
    // Calculated onChange for surfaceSpeed instead
    dependencies: keys('surfaceSpeed', 'geometry_cuttingDiameter', 'unit'),
    compute: (values: PresetData) => {
      const metric = values?.unit !== 'in'
      const v = values?.surfaceSpeed // m/min or ft/min
      const d = values?.geometry_cuttingDiameter // inches or mm
      return metric
        ? (v * 1000) / (Math.PI * d) // V (m/min), d (mm) → N (rpm)
        : (v * 12) / (Math.PI * d) // V (ft/min), d (in) → N (rpm)
    },
    computeAlways: true,
    */
  }

  surfaceSpeed?: number
  static surfaceSpeed_spec: FieldSpec = {
    title: 'Surface Speed',
    kind: 'surface_speed',
    defaultValue: undefined,
    enabledWhen: typeIn(
      'ToolMilling',
      'ToolHoleMaking',
      'turning general',
      'turning boring',
      'turning grooving',
    ),
    gt: 0,
    required: false, // always calculated
    onChange(values: PresetData, oldValue, newValue, setFieldValue) {
      const metric = values?.unit !== 'in'
      const v = newValue // m/min or ft/min
      const d = values?.geometry_cuttingDiameter ?? NaN // inches or mm
      const spindleSpeed = metric
        ? (v * 1000) / (Math.PI * d) // V (m/min), d (mm) → N (rpm)
        : (v * 12) / (Math.PI * d) // V (ft/min), d (in) → N (rpm)
      setFieldValue('spindleSpeed', spindleSpeed)
    },
    dependencies: keys('spindleSpeed', 'geometry_cuttingDiameter', 'unit'),
    compute: (values: PresetData) => {
      //const parent = values?.__parent;
      const metric = values?.unit !== 'in'
      const n = values?.spindleSpeed // rpm
      const d = values?.geometry_cuttingDiameter ?? NaN // inches or mm
      return metric
        ? (d * Math.PI * n) / 1000 // meter/minute
        : (d * Math.PI * n) / 12 // ft/min
    },
    computeAlways: true,
  }

  rampSpindleSpeed?: number
  static rampSpindleSpeed_spec: FieldSpec = {
    title: 'Ramp Spindle Speed',
    kind: 'rotation_speed',
    defaultValue: 5000, // TODO: Remove to ensure we use callback
    enabledWhen: typeIn('ToolMilling'),
    gt: 0,
    required: true,
    dependencies: keys('spindleSpeed'),
    compute: (values: PresetData) => values?.spindleSpeed,
  }

  //////////// preset_feedrates ////////////
  static groupFeedrates_spec: FieldSpec = {
    title: 'Feedrates',
    kind: 'group',
  }

  feedCutting?: number
  static feedCutting_spec: FieldSpec = {
    title: 'Cutting Feedrate',
    kind: 'feedrate',
    defaultValueMM: 1000,
    defaultValueIN: 40,
    inGroup: true,
    enabledWhen: typeIn(
      'ToolMilling',
      'ToolCutting',
      'ToolDepositing',
      'boring bar',
      'counter bore',
      'center drill',
      'spot drill',
      'counter sink',
      'turning general',
      'turning boring',
    ),
    gt: 0,
    required: true,
    /*
    dependencies: keys('feedPerTooth', 'spindleSpeed', 'geometry_numberOfFlutes'),
    compute: (values: PresetData) => {
      const feedPerTooth = values?.feedPerTooth
      const spindleSpeed = values?.spindleSpeed
      const numberOfFlutes = values?.geometry_numberOfFlutes
      return feedPerTooth * spindleSpeed * numberOfFlutes
    },
    */
  }

  feedPerTooth?: number
  static feedPerTooth_spec: FieldSpec = {
    title: 'Feed per Tooth',
    kind: 'length',
    defaultValue: undefined,
    inGroup: true,
    enabledWhen: typeIn(
      'ToolMilling',
      'boring bar',
      'counter bore',
      'center drill',
      'spot drill',
      'counter sink',
    ),
    gt: 0,
    required: false, // always calculated
    dependencies: keys(
      'feedCutting',
      'spindleSpeed',
      'geometry_numberOfFlutes',
    ),
    compute: (values: PresetData) => {
      const feedCutting = values?.feedCutting ?? NaN
      const spindleSpeed = values?.spindleSpeed
      const numberOfFlutes = values?.geometry_numberOfFlutes ?? NaN
      return feedCutting / (spindleSpeed * numberOfFlutes)
    },
    computeAlways: true,
    onChange(values: PresetData, oldValue, newValue, setFieldValue) {
      const spindleSpeed = values?.spindleSpeed
      const numberOfFlutes = values?.geometry_numberOfFlutes ?? NaN
      const feedCutting = newValue * spindleSpeed * numberOfFlutes
      setFieldValue('feedCutting', feedCutting)
      setFieldValue('feedEntry', feedCutting)
      setFieldValue('feedExit', feedCutting)
      setFieldValue('feedTransition', feedCutting)
    },
  }

  feedEntry?: number
  static feedEntry_spec: FieldSpec = {
    title: 'Lead-In Feedrate',
    kind: 'feedrate',
    defaultValueMM: 1000,
    defaultValueIN: 40,
    enabledWhen: typeIn(
      'ToolMilling',
      'ToolCutting',
      'ToolDepositing',
      'boring bar',
      'counter bore',
      'center drill',
      'spot drill',
      'counter sink',
      'turning general',
      'turning boring',
      'probe',
    ),
    gt: 0,
    required: true,
    dependencies: keys('feedCutting'),
    compute: (values: PresetData) => values?.feedCutting,
  }

  feedExit?: number
  static feedExit_spec: FieldSpec = {
    title: 'Lead-Out Feedrate',
    kind: 'feedrate',
    defaultValueMM: 1000,
    defaultValueIN: 40,
    enabledWhen: typeIn(
      'ToolMilling',
      'ToolCutting',
      'ToolDepositing',
      'boring bar',
      'counter bore',
      'center drill',
      'spot drill',
      'counter sink',
      'turning general',
      'turning boring',
    ),
    gt: 0,
    required: true,
    dependencies: keys('feedCutting'),
    compute: (values: PresetData) => values?.feedCutting,
  }

  feedTransition?: number
  static feedTransition_spec: FieldSpec = {
    title: 'Transition Feedrate',
    kind: 'feedrate',
    defaultValueMM: 1000,
    defaultValueIN: 40,
    enabledWhen: typeIn(
      'ToolMilling',
      'boring bar',
      'counter bore',
      'center drill',
      'spot drill',
      'counter sink',
    ),
    gt: 0,
    required: true,
    dependencies: keys('feedCutting'),
    compute: (values: PresetData) => values?.feedCutting,
  }

  feedRamp?: number
  static feedRamp_spec: FieldSpec = {
    title: 'Ramp Feedrate',
    kind: 'feedrate',
    defaultValueMM: 500,
    defaultValueIN: 20,
    enabledWhen: typeIn(
      'ToolMilling',
      'boring bar',
      'counter bore',
      'center drill',
      'spot drill',
      'counter sink',
    ),
    gt: 0,
    required: true,
    dependencies: keys('feedCutting'),
    compute: (values: PresetData) => values?.feedCutting ?? NaN / 2,
  }

  /*
  feedEntryRel?: number
  static feedEntryRel_spec: FieldSpec = {
    title: 'Lead-In Feed per Revolution',
    kind: 'feedrate',
    defaultValueMM: 1.0,
    defaultValueIN: 0.04,
  }

  feedCuttingRel?: number
  static feedCuttingRel_spec: FieldSpec = {
    title: 'Cutting Feed per Revolution',
    kind: 'feedrate_mm',
  }

  feedDepositing?: number
  static feedDepositing_spec: FieldSpec = {
    title: 'Depositing Feedrate',
    kind: 'feedrate',
    defaultValueMM: 1000,
  }
*/

  feedExitRel?: number
  static feedExitRel_spec: FieldSpec = {
    title: 'Lead-Out Feed per Revolution',
    kind: 'feedrate',
    defaultValueMM: 1.0,
    defaultValueIN: 0.04,
    enabledWhen: typeIn(
      'turning general',
      'turning boring',
      'turning grooving',
    ),
    gt: 0,
    required: true,
  }

  feedProbeLink?: number
  static feedProbeLink_spec: FieldSpec = {
    title: 'Link Feedrate',
    kind: 'feedrate',
    defaultValueMM: 3000,
    defaultValueIN: 120,
    enabledWhen: typeIn('probe'),
    gt: 0,
    required: true,
  }

  feedProbeMeasure?: number
  static feedProbeMeasure_spec: FieldSpec = {
    title: 'Measure Feedrate',
    kind: 'feedrate',
    defaultValueMM: 100,
    defaultValueIN: 4.0,
    enabledWhen: typeIn('probe'),
    gt: 0,
    required: true,
  }

  //////////// preset_feedrates_vertical ////////////
  static groupVerticalFeedrates_spec: FieldSpec = {
    title: 'Vertical Feedrates',
    kind: 'group',
  }

  useFeedPerRevolution?: boolean
  static useFeedPerRevolution_spec: FieldSpec = {
    title: 'Use Feed per Revolution',
    kind: 'boolean',
    defaultValue: false,
    enabledWhen: typeIn(
      'ToolDrill',
      'reamer',
      'counter sink',
      'turning general',
      'turning boring',
      'turning grooving',
    ),
    required: true,
  }

  feedPlunge?: number
  static feedPlunge_spec: FieldSpec = {
    title: 'Plunge Feedrate',
    kind: 'feedrate',
    defaultValueMM: 500,
    defaultValueIN: 20,
    enabledWhen: typeIn(
      'ToolMilling',
      'ToolDrill',
      'ToolDepositing',
      'boring bar',
      'counter bore',
      'reamer',
      'counter sink',
    ),
    gt: 0,
    required: true,
  }

  feedPerRevolution?: number
  static feedPerRevolution_spec: FieldSpec = {
    title: 'Plunge Feed per Revolution',
    kind: 'length',
    defaultValue: undefined,
    enabledWhen: typeIn(
      'ToolMilling',
      'ToolDrill',
      'boring bar',
      'counter bore',
      'reamer',
      'counter sink',
      'turning general',
      'turning boring',
      'turning grooving',
    ),
    gt: 0,
    required: false,
    computeAlways: true,
    dependencies: keys('spindleSpeed', 'feedPlunge'),
    compute(values: PresetData) {
      return values?.feedPlunge ?? NaN / values?.spindleSpeed
    },
    onChange(values, oldValue, newValue, setFieldValue) {
      setFieldValue('feedPlunge', newValue * values?.spindleSpeed)
    },
  }

  feedRetract?: number
  static feedRetract_spec: FieldSpec = {
    title: 'Retract Feedrate',
    kind: 'feedrate',
    defaultValueMM: 1500,
    defaultValueIN: 60,
    enabledWhen: typeIn('ToolDrill', 'reamer', 'counter sink'),
    gt: 0,
    required: true,
  }

  feedRetractPerRevolution?: number
  static feedRetractPerRevolution_spec: FieldSpec = {
    title: 'Retract Feed per Revolution',
    kind: 'length',
    defaultValueMM: 1.0,
    defaultValueIN: 0.04,
    enabledWhen: typeIn('ToolDrill', 'reamer', 'counter sink'),
    gt: 0,
    required: true,
  }

  //////////// preset_capabilities ////////////
  static groupCapabilities_spec: FieldSpec = {
    title: 'Passes and Linking',
    kind: 'group',
  }

  useStepdown?: boolean
  static useStepdown_spec: FieldSpec = {
    title: 'Use Stepdown',
    kind: 'boolean',
    defaultValue: false,
    enabledWhen: typeIn('ToolMilling'),
    required: true,
  }

  stepdown?: number
  static stepdown_spec: FieldSpec = {
    title: 'Stepdown',
    kind: 'length',
    defaultValueMM: 6,
    defaultValueIN: 0.5,
    //enabledWhen: typeIn('ToolMilling'),
    enabledWhen: { useStepdown: { eq: true } },
    gt: 0,
    required: true,
  }

  useStepover?: boolean
  static useStepover_spec: FieldSpec = {
    title: 'Use Stepover',
    kind: 'boolean',
    defaultValue: false,
    enabledWhen: typeIn('ToolMilling', 'ToolDepositing'),
    required: true,
  }

  stepover?: number
  static stepover_spec: FieldSpec = {
    title: 'Stepover',
    kind: 'length',
    defaultValueMM: 1,
    defaultValueIN: 0.04,
    //enabledWhen: typeIn('ToolMilling', 'ToolDepositing'),
    enabledWhen: { useStepover: { eq: true } },
    gt: 0,
    required: true,
  }

  useDepthOfCut?: boolean
  static useDepthOfCut_spec: FieldSpec = {
    title: 'Use Depth of Cut',
    kind: 'boolean',
    defaultValue: false,
    enabledWhen: typeIn('turning general', 'turning boring'),
    required: true,
  }

  depthOfCut?: number
  static depthOfCut_spec: FieldSpec = {
    title: 'Depth of Cut',
    kind: 'length',
    defaultValueMM: 1,
    defaultValueIN: 0.04,
    enabledWhen: typeIn('turning general', 'turning boring'),
    gt: 0,
    required: true,
  }

  static version: number = 1
}

// helper to get type checked keys
function keys<K extends keyof PresetData>(...args: K[]): K[] {
  return args
}

export type PresetData = InstanceProperties<FSPreset>
